import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { MachineCard } from '@/components/MachineCard';
import { useGetPromotedMachines, useQuickstart } from '@/lib/api/orval/target/machines/machines';
import { useSubscribeNewsletter } from '@/lib/api/orval/target/site/site';
import { errorMessage, guestMachineLimit, type GuestMachineLimit } from '@/lib/errors';
import { xpraPath } from '@/lib/xpra';

export function Home(): React.ReactElement {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPromotedMachines();

  const [limit, setLimit] = useState<GuestMachineLimit | null>(null);
  const quickstart = useQuickstart({
    mutation: {
      onSuccess: (result) => {
        setLimit(null);
        navigate(xpraPath(result.wss_uri), {
          state: { wssUri: result.wss_uri, machine: result.machine },
        });
      },
      onError: (e) => setLimit(guestMachineLimit(e)),
    },
  });

  const [email, setEmail] = useState('');
  const [newsletterMsg, setNewsletterMsg] = useState('');
  const subscribe = useSubscribeNewsletter({
    mutation: {
      onSuccess: () => {
        setNewsletterMsg('Subscribed — thanks!');
        setEmail('');
      },
      onError: (e) => setNewsletterMsg(errorMessage(e)),
    },
  });

  return (
    <div className="flex flex-col gap-10">
      <section className="text-center flex flex-col gap-4 items-center">
        <h1 className="text-4xl font-bold">Forkable Linux desktops, in your browser.</h1>
        <p className="max-w-xl" style={{ color: 'var(--text-secondary)' }}>
          Launch a disposable desktop in one click, fork anyone&apos;s machine, and share a
          live session with a secret link.
        </p>
        <button
          className="btn-primary text-lg px-6 py-3"
          onClick={() => quickstart.mutate({ data: {} })}
          disabled={quickstart.isPending}
        >
          {quickstart.isPending ? 'Starting…' : 'Try a demo machine →'}
        </button>
        {limit ? (
          <div className="themed-card p-4 flex flex-col gap-2 items-center">
            <p>
              You already have a demo machine,{' '}
              <Link to={`/m/${limit.existing_machine.public_id}`}>
                {limit.existing_machine.name}
              </Link>
              . Starting another replaces it.
            </p>
            <button
              className="btn-primary"
              onClick={() => quickstart.mutate({ data: { replace: true } })}
              disabled={quickstart.isPending}
            >
              {quickstart.isPending ? 'Replacing…' : 'Replace it'}
            </button>
          </div>
        ) : (
          quickstart.isError && (
            <p style={{ color: 'var(--danger)' }}>{errorMessage(quickstart.error)}</p>
          )
        )}
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4">Featured machines</h2>
        {isLoading && <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>}
        {error ? <p style={{ color: 'var(--danger)' }}>{errorMessage(error)}</p> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          {data?.machines.map((machine) => (
            <MachineCard key={machine.public_id} machine={machine} />
          ))}
        </div>
        {data && data.machines.length === 0 && (
          <p style={{ color: 'var(--text-secondary)' }}>No featured machines yet.</p>
        )}
      </section>

      <section className="themed-card p-6 flex flex-col gap-3 max-w-lg">
        <h2 className="text-xl font-semibold">Stay in the loop</h2>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            subscribe.mutate({ data: { email } });
          }}
        >
          <input
            className="themed-input"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button className="btn-primary" type="submit" disabled={subscribe.isPending}>
            Subscribe
          </button>
        </form>
        {newsletterMsg && (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {newsletterMsg}
          </p>
        )}
      </section>
    </div>
  );
}
